/**
 * Notification Policy — the project-level contract (`project_config.notifications`).
 *
 * The policy is the one place that decides whether a notification is sent, held, folded into a
 * digest or suppressed: per-user group/type toggles, a "check-in level", quiet hours, and "the
 * user's rule beats the standard notification". Plan:
 * docs/platform/notifications/planned/frank-notifications-and-money-rules.md.
 *
 * **Off unless switched on.** A project without `notifications.enabled === true` behaves exactly as
 * before the policy existed: every notification goes straight to Novu, nothing is logged, nothing
 * is held. Only templates a catalogue type claims are ever managed; every other template (OTPs,
 * password resets, invites, statements, KYC, …) passes through untouched even when the policy is on.
 */
import { NotificationTemplateEnum } from './notification.type';

export type NotificationSeverity = 'critical' | 'warning' | 'info';

export type NotificationCheckInLevel = 'onlyWhenItMatters' | 'balanced' | 'keepMePosted';

export const NOTIFICATION_CHECK_IN_LEVELS: readonly NotificationCheckInLevel[] = [
  'onlyWhenItMatters',
  'balanced',
  'keepMePosted',
];

/** Product tier from the design board; informational only (the builder and apps may filter on it). */
export type NotificationTypeTier = 'mvp' | 'phase2' | 'tbc' | 'vision';

export type NotificationTypeDefinition = {
  /** Unique across the catalogue, conventionally `<group>.<name>` (e.g. `money.in`). */
  key: string;
  label: string;
  description?: string;
  tier?: NotificationTypeTier;
  /** Default for a user who never touched it. Unset = on. */
  defaultOn?: boolean;
  severity?: NotificationSeverity;
  /**
   * Which existing sends belong to this type. A send matches when its template is listed and, if
   * `codes` is set, its `payload.code` is one of them (used to split `companion-nudge` by detector).
   * Resolution is two passes over the catalogue in order: first the types whose `match` lists `codes`,
   * then the rest, so a code-less catch-all for a template never shadows a code-specific type in a
   * later group. A type with no `match` only receives candidates submitted with its key directly (rules, reports,
   * news).
   */
  match?: { templates?: NotificationTemplateEnum[]; codes?: string[] };
};

export type NotificationGroupDefinition = {
  /** Well-known keys carry behaviour: `safety` (locked), `rules` (user's own rules), `reports`, `news`. */
  key: string;
  label: string;
  description?: string;
  /** Always delivered; the user cannot switch it off. */
  locked?: boolean;
  /** Default for a user who never touched it. Unset = on. */
  defaultOn?: boolean;
  types: NotificationTypeDefinition[];
};

/** One cell of the check-in table: how a group (or one type in it) behaves at a level. */
export type NotificationCheckInRule = {
  /** `send` = deliver now; `digest` = fold into the next report; `suppress` = drop (logged). */
  outcome?: 'send' | 'digest' | 'suppress';
  /** Below this severity the candidate is digested instead of sent. */
  minSeverity?: NotificationSeverity;
  /** Max sends per user per (user-local) day for this group; over budget → digest. */
  dailyMax?: number;
  /** Max sends per user per rolling 7 days for this group; over budget → digest. */
  weeklyMax?: number;
  /**
   * `inAppOnly` skips the push step. Only honoured by policy-originated sends on the generic workflow
   * (rules, reports, news, heads-ups); provider templates have no per-send channel switch, so for
   * them `inAppOnly` is treated as `send` and logged as such.
   */
  channels?: 'all' | 'inAppOnly';
};

export type NotificationCheckInGroupRules = NotificationCheckInRule & {
  types?: Record<string, NotificationCheckInRule>;
};

export type NotificationCheckInTable = Record<NotificationCheckInLevel, Record<string, NotificationCheckInGroupRules>>;

export type NotificationQuietHours = {
  /** `HH:mm`, user-local. */
  start: string;
  end: string;
  /** IANA zone used when the user has none recorded. */
  timeZone: string;
};

export type NotificationsPolicyConfig = {
  /** The master switch. Absent/false = the platform behaves exactly as it did before the policy. */
  enabled?: boolean;
  /** Absent = `DEFAULT_NOTIFICATION_GROUPS`. */
  groups?: NotificationGroupDefinition[];
  /** Absent = `keepMePosted`. */
  defaultCheckInLevel?: NotificationCheckInLevel;
  /** Per-level overrides, merged over `DEFAULT_NOTIFICATION_CHECK_IN_TABLE` group by group. */
  checkInLevels?: Partial<NotificationCheckInTable>;
  /** Absent/null = no quiet hours. `info` items are held until the window ends; `critical` always goes. */
  quietHours?: NotificationQuietHours | null;
  /**
   * Seconds a money-in/out notification waits before it is decided, so the user's own notify-rule for
   * the same transaction can claim it ("rule wins"). Absent = 10. 0 = decide immediately.
   */
  moneyHoldSeconds?: number;
  /**
   * Who owns the insight-style heads-ups (low balance, unusual spend, …). A candidate from a source
   * that is not the owner is dropped (`not-owner`), so turning a second insight provider on later
   * cannot double-notify. Applies to the `headsUp` group only. Absent = `companion`. `none` = no insight heads-ups at all.
   */
  sources?: { insights?: 'companion' | 'tink' | 'none' };
};

/** Templates the default catalogue claims — everything else always passes through. */
export const DEFAULT_NOTIFICATION_GROUPS: NotificationGroupDefinition[] = [
  {
    key: 'safety',
    label: 'Keeping you safe',
    description: 'Security, approvals and anything that needs your yes. Always on.',
    locked: true,
    types: [
      {
        key: 'safety.devices',
        label: 'New device and security changes',
        tier: 'mvp',
        severity: 'critical',
        match: {
          templates: [
            NotificationTemplateEnum.DEVICE_ENROLLED,
            NotificationTemplateEnum.DEVICE_REMOVED,
            NotificationTemplateEnum.PASSWORD_CHANGED_EMAIL,
          ],
        },
      },
      {
        key: 'safety.approvals',
        label: 'Payment approvals and step-up checks',
        tier: 'mvp',
        severity: 'critical',
        match: {
          templates: [
            NotificationTemplateEnum.THREEDS_CHALLENGE,
            NotificationTemplateEnum.BULK_PAYMENT_REQUEST_APPROVAL,
          ],
        },
      },
      { key: 'safety.ruleApproval', label: 'A rule needs your yes', tier: 'mvp', severity: 'critical' },
      {
        // Locked: an agreement to take money, and each notice ahead of a collection, always go now.
        key: 'safety.paymentAgreements',
        label: 'Payment agreements and upcoming collections',
        tier: 'mvp',
        severity: 'critical',
        match: {
          templates: [
            NotificationTemplateEnum.VRP_MANDATE_SETUP_CONFIRMATION,
            NotificationTemplateEnum.VRP_MANDATE_ADVANCE_NOTICE,
          ],
        },
      },
    ],
  },
  {
    key: 'money',
    label: 'Money in & out',
    types: [
      {
        key: 'money.in',
        label: 'Money in',
        tier: 'mvp',
        severity: 'info',
        match: { templates: [NotificationTemplateEnum.INBOUND_NOTIFICATION] },
      },
      {
        key: 'money.out',
        label: 'Money out and card payments',
        tier: 'mvp',
        severity: 'info',
        match: { templates: [NotificationTemplateEnum.OUTBOUND_NOTIFICATION] },
      },
      {
        key: 'money.internal',
        label: 'Transfers between your accounts',
        tier: 'mvp',
        severity: 'info',
        match: { templates: [NotificationTemplateEnum.INTERNAL_TRANSFER] },
      },
      {
        key: 'money.failed',
        label: "Something didn't go through",
        tier: 'mvp',
        severity: 'warning',
        match: { templates: [NotificationTemplateEnum.PAYMENT_REJECTED] },
      },
      {
        key: 'money.other',
        label: 'Savings and investment movements',
        tier: 'mvp',
        severity: 'info',
        match: { templates: [NotificationTemplateEnum.TRANSACTION_NOTIFICATION] },
      },
      {
        key: 'money.conversions',
        label: 'Currency conversions',
        tier: 'phase2',
        severity: 'info',
        match: {
          templates: [NotificationTemplateEnum.EXCHANGE_COMPLETED, NotificationTemplateEnum.EXCHANGE_FAILED],
        },
      },
    ],
  },
  {
    key: 'headsUp',
    label: 'Heads-ups',
    description: 'Low balance, unusual spending and suggestions.',
    types: [
      {
        key: 'headsUp.lowBalance',
        label: 'Low balance',
        tier: 'mvp',
        severity: 'warning',
        match: { templates: [NotificationTemplateEnum.COMPANION_NUDGE], codes: ['low-balance-projection'] },
      },
      {
        key: 'headsUp.unusualSpend',
        label: 'Unusual spend and duplicate charges',
        tier: 'mvp',
        severity: 'warning',
        match: { templates: [NotificationTemplateEnum.COMPANION_NUDGE], codes: ['unusual-spend', 'duplicate-charge'] },
      },
      {
        key: 'headsUp.credits',
        label: 'Large or first-time money in',
        tier: 'mvp',
        severity: 'info',
        match: { templates: [NotificationTemplateEnum.COMPANION_NUDGE], codes: ['large-credit', 'first-time-credit'] },
      },
      {
        key: 'headsUp.suggestions',
        label: 'Rule suggestions',
        tier: 'mvp',
        severity: 'info',
        match: { templates: [NotificationTemplateEnum.COMPANION_NUDGE], codes: ['recurring-detected', 'rule-suggestion'] },
      },
      {
        // Any other companion nudge code: still a heads-up, still switchable.
        key: 'headsUp.other',
        label: 'Other heads-ups',
        tier: 'mvp',
        severity: 'info',
        match: { templates: [NotificationTemplateEnum.COMPANION_NUDGE] },
      },
    ],
  },
  {
    key: 'rules',
    label: 'Money rules',
    description: 'Your own "notify me when…" rules. Pause or delete a rule to stop it; there is no switch here.',
    locked: true,
    types: [{ key: 'rules.notify', label: 'All money rules', tier: 'mvp', severity: 'info' }],
  },
  {
    key: 'reports',
    label: 'Reports & insights',
    types: [
      {
        key: 'reports.weekly',
        label: 'Weekly report',
        tier: 'mvp',
        severity: 'info',
        match: { templates: [NotificationTemplateEnum.COMPANION_NUDGE], codes: ['report-weekly'] },
      },
      {
        key: 'reports.monthly',
        label: 'Monthly wrap',
        tier: 'mvp',
        severity: 'info',
        match: { templates: [NotificationTemplateEnum.COMPANION_NUDGE], codes: ['report-monthly'] },
      },
      /** account-service's weekly in-app summary of digested items, for projects without companion weekly reports. */
      { key: 'reports.digest', label: 'While you were away', tier: 'mvp', severity: 'info' },
    ],
  },
  {
    key: 'news',
    label: 'News',
    types: [
      { key: 'news.updates', label: 'Updates for you', tier: 'mvp', severity: 'info' },
      { key: 'news.product', label: 'Product news and new features', tier: 'mvp', severity: 'info' },
      { key: 'news.offers', label: 'Perks and partner offers', tier: 'phase2', severity: 'info', defaultOn: false },
    ],
  },
];

/** The design board's table (§6 of the plan). Groups not listed at a level are sent as-is. */
export const DEFAULT_NOTIFICATION_CHECK_IN_TABLE: NotificationCheckInTable = {
  onlyWhenItMatters: {
    money: { outcome: 'digest', types: { 'money.failed': { outcome: 'send' } } },
    headsUp: { minSeverity: 'warning', dailyMax: 1 },
    reports: { channels: 'inAppOnly' },
    news: { channels: 'inAppOnly' },
  },
  balanced: {
    money: {
      types: {
        'money.out': { channels: 'inAppOnly' },
        'money.other': { channels: 'inAppOnly' },
        'money.conversions': { channels: 'inAppOnly' },
      },
    },
    headsUp: { dailyMax: 2 },
    reports: { types: { 'reports.digest': { channels: 'inAppOnly' } } },
    news: { weeklyMax: 1 },
  },
  keepMePosted: {
    headsUp: { dailyMax: 5 },
    reports: { types: { 'reports.digest': { channels: 'inAppOnly' } } },
  },
};

export const DEFAULT_NOTIFICATION_MONEY_HOLD_SECONDS = 10;

/** The user's saved choices. A missing key means "the catalogue default". */
export type UserNotificationSettings = {
  groups: Record<string, boolean>;
  types: Record<string, boolean>;
  checkInLevel?: NotificationCheckInLevel;
};

/** What the app reads: the catalogue with each switch resolved for this user. */
export type ResolvedNotificationSettings = {
  enabled: boolean;
  checkInLevel: NotificationCheckInLevel;
  groups: Array<{
    key: string;
    label: string;
    description?: string;
    locked: boolean;
    on: boolean;
    types: Array<{ key: string; label: string; description?: string; tier?: NotificationTypeTier; on: boolean }>;
  }>;
};

/** Group catalogue in force for a project (config wins, else the default). */
export function notificationGroupsFor(config?: NotificationsPolicyConfig | null): NotificationGroupDefinition[] {
  return config?.groups?.length ? config.groups : DEFAULT_NOTIFICATION_GROUPS;
}

export function notificationPolicyEnabled(config?: NotificationsPolicyConfig | null): boolean {
  return config?.enabled === true;
}
